import { useMemo, useState } from "react";
import {
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody,
  Input, VStack, HStack, Button, Box, InputGroup, InputLeftElement,
  Icon, Portal,
} from "@chakra-ui/react";
import CDS from "design-system";
import { FiSearch, FiPlus } from "react-icons/fi";

export interface Item {
  id: number;
  name: string;
  subtitle?: string | null;
  isHidden: boolean;
}

interface Props {
  trigger: React.ReactElement;
  title: string;
  placeholder: string;
  emptyLabel: string;
  items: Item[];
  onAdd: (id: number) => void;
}

export default function AddItemPopover({ trigger, title, placeholder, emptyLabel, items, onAdd }: Props) {
  const [search, setSearch] = useState("");

  const hiddenItems = useMemo(() => items.filter((i) => i.isHidden), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return hiddenItems;
    return hiddenItems.filter((i) => i.name.toLowerCase().includes(q));
  }, [hiddenItems, search]);

  const hasHidden = hiddenItems.length > 0;

  return (
    <Popover placement="top-start" isLazy>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent w="320px">
          <PopoverArrow />
          <PopoverBody p={3}>
            <VStack align="stretch" spacing={3}>
              <CDS.Text12 fontWeight="semibold">{title}</CDS.Text12>
              {hasHidden ? (
                <>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder={placeholder}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                  <Box maxH="240px" overflowY="auto">
                    <VStack align="stretch" spacing={1}>
                      {filtered.length === 0 ? (
                        <CDS.Text12 color="gray.500" textAlign="center" py={4}>
                          No matches
                        </CDS.Text12>
                      ) : filtered.map((item) => (
                        <HStack key={item.id} justify="space-between" px={2} py={1} borderRadius="md" _hover={{ bg: "gray.50" }} align="center" spacing={2}>
                          <VStack align="start" spacing={0} flex={1} minW={0}>
                            <CDS.Text12 noOfLines={1} w="100%" color="gray.900">{item.name}</CDS.Text12>
                            {item.subtitle && (
                              <CDS.Text10 color="gray.500" noOfLines={1} w="100%">
                                {item.subtitle}
                              </CDS.Text10>
                            )}
                          </VStack>
                          <Button size="xs" leftIcon={<FiPlus />} variant="ghost" colorScheme="blue" onClick={() => onAdd(item.id)} flexShrink={0}>
                            Add
                          </Button>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </>
              ) : (
                <CDS.Text12 color="gray.500" textAlign="center" py={4}>
                  {emptyLabel}
                </CDS.Text12>
              )}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
